import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebaseConfig';
import { IoAdd, IoTrash, IoCreate, IoCheckmark, IoClose, IoImage, IoSearch } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { ImageCropModal } from './ImageCropModal';

// Utility function to remove undefined values from objects (Firestore doesn't support undefined)
const removeUndefinedValues = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item));
  }
  if (obj !== null && typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefinedValues(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

// Utility function to safely convert values to Date objects
const toDate = (value: any): Date => {
  if (!value) return new Date();
  // Check if it's a Firestore Timestamp (has toDate method)
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  // Check if it's already a Date
  if (value instanceof Date) {
    return value;
  }
  // Try to convert to Date
  try {
    return new Date(value);
  } catch {
    return new Date();
  }
};

interface ProductVariant {
  id: string;
  name: string;
  price?: number;
  priceType?: 'fixed' | 'range';
  priceRange?: { min: number; max: number };
  baseStock: string;
  stockType?: 'in-stock' | 'order-basis';
  sku?: string;
  image?: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  categoryId: string;
  categoryName?: string;
  brand: string;
  price?: number;
  priceType?: 'fixed' | 'range';
  priceRange?: { min: number; max: number };
  baseStock: string; // Changed from number to string to support "Order Basis"
  stockType?: 'in-stock' | 'order-basis';
  hasVariants: boolean;
  variants?: ProductVariant[];
  images: string[];
  specifications: { [key: string]: string };
  isPublished: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface Category {
  id: string;
  name: string;
  isActive: boolean;
}

export const PublishProductManager: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [itemsToShow, setItemsToShow] = useState(50);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    brand: '',
    price: '',
    priceType: 'fixed' as 'fixed' | 'range',
    priceRangeMin: '',
    priceRangeMax: '',
    baseStock: '0',
    stockType: 'in-stock' as 'in-stock' | 'order-basis',
    hasVariants: false,
    variants: [] as ProductVariant[],
    images: [] as string[],
    specifications: {} as { [key: string]: string },
    isPublished: true
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [specKey, setSpecKey] = useState('');
  const [specValue, setSpecValue] = useState('');
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingVariantImageFile, setPendingVariantImageFile] = useState<File | null>(null);
  const [showVariantCropModal, setShowVariantCropModal] = useState(false);
  const [uploadingVariantImage, setUploadingVariantImage] = useState(false);
  
  // Variant management
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);
  const [variantForm, setVariantForm] = useState({
    name: '',
    sku: '',
    price: '',
    priceType: 'fixed' as 'fixed' | 'range',
    priceRangeMin: '',
    priceRangeMax: '',
    baseStock: '0',
    stockType: 'in-stock' as 'in-stock' | 'order-basis',
    image: ''
  });

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const categoriesRef = collection(db, 'published_categories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const productsRef = collection(db, 'published_products');
      const q = query(productsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const productsData = snapshot.docs.map(doc => {
        const data = doc.data();
        const category = categories.find(c => c.id === data.categoryId);
        return {
          id: doc.id,
          ...data,
          categoryName: category?.name || 'Unknown',
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt)
        };
      }) as Product[];
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [categories]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch products when categories change
  useEffect(() => {
    if (categories.length > 0) {
      fetchProducts();
    }
  }, [categories, fetchProducts]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Show crop modal for the first file
    const file = files[0];
    setPendingImageFile(file);
    setShowCropModal(true);
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    setUploadingImage(true);
    try {
      // Convert the cropped blob URL to a blob
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      
      const timestamp = Date.now();
      const fileName = pendingImageFile?.name || 'image.jpg';
      const storageRef = ref(storage, `products/${timestamp}_${fileName}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      
      setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      setShowCropModal(false);
      setPendingImageFile(null);
    }
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    setPendingImageFile(null);
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleVariantImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    setPendingVariantImageFile(file);
    setShowVariantCropModal(true);
  };

  const handleVariantCropComplete = async (croppedImageUrl: string) => {
    setUploadingVariantImage(true);
    try {
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      
      const timestamp = Date.now();
      const fileName = pendingVariantImageFile?.name || 'variant.jpg';
      const storageRef = ref(storage, `variants/${timestamp}_${fileName}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      
      setVariantForm(prev => ({ ...prev, image: url }));
      toast.success('Variant image uploaded');
    } catch (error) {
      console.error('Error uploading variant image:', error);
      toast.error('Failed to upload variant image');
    } finally {
      setUploadingVariantImage(false);
      setShowVariantCropModal(false);
      setPendingVariantImageFile(null);
    }
  };

  const handleVariantCancelCrop = () => {
    setShowVariantCropModal(false);
    setPendingVariantImageFile(null);
  };

  const handleRemoveVariantImage = () => {
    setVariantForm(prev => ({ ...prev, image: '' }));
  };

  const handleAddSpecification = () => {
    if (!specKey.trim() || !specValue.trim()) {
      toast.error('Please enter both key and value');
      return;
    }
    setFormData(prev => ({
      ...prev,
      specifications: { ...prev.specifications, [specKey]: specValue }
    }));
    setSpecKey('');
    setSpecValue('');
  };

  const handleRemoveSpecification = (key: string) => {
    setFormData(prev => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[key];
      return { ...prev, specifications: newSpecs };
    });
  };

  const handleAddProduct = async () => {
    if (!formData.name.trim() || !formData.categoryId || !formData.brand.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const productsRef = collection(db, 'published_products');
      const productData: any = {
        name: formData.name,
        description: formData.description,
        categoryId: formData.categoryId,
        brand: formData.brand,
        priceType: formData.priceType,
        stockType: formData.stockType,
        baseStock: formData.stockType === 'order-basis' ? 'Order Basis' : formData.baseStock,
        hasVariants: formData.hasVariants,
        variants: formData.hasVariants ? formData.variants : [],
        images: formData.images,
        specifications: formData.specifications,
        isPublished: formData.isPublished,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Handle price based on type (only if no variants)
      if (!formData.hasVariants) {
        if (formData.priceType === 'fixed') {
          productData.price = formData.price ? parseFloat(formData.price) : null;
          productData.priceRange = null;
        } else {
          productData.price = null;
          productData.priceRange = {
            min: formData.priceRangeMin ? parseFloat(formData.priceRangeMin) : 0,
            max: formData.priceRangeMax ? parseFloat(formData.priceRangeMax) : 0
          };
        }
      } else {
        // If has variants, clear base price
        productData.price = null;
        productData.priceRange = null;
      }

      // Remove undefined values before saving to Firestore
      const cleanedData = removeUndefinedValues(productData);
      await addDoc(productsRef, cleanedData);
      
      toast.success('Product added successfully');
      setShowAddModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Failed to add product');
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct || !formData.name.trim() || !formData.categoryId || !formData.brand.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const productRef = doc(db, 'published_products', editingProduct.id);
      const productData: any = {
        name: formData.name,
        description: formData.description,
        categoryId: formData.categoryId,
        brand: formData.brand,
        priceType: formData.priceType,
        stockType: formData.stockType,
        baseStock: formData.stockType === 'order-basis' ? 'Order Basis' : formData.baseStock,
        hasVariants: formData.hasVariants,
        variants: formData.hasVariants ? formData.variants : [],
        images: formData.images,
        specifications: formData.specifications,
        isPublished: formData.isPublished,
        updatedAt: new Date()
      };

      // Handle price based on type (only if no variants)
      if (!formData.hasVariants) {
        if (formData.priceType === 'fixed') {
          productData.price = formData.price ? parseFloat(formData.price) : null;
          productData.priceRange = null;
        } else {
          productData.price = null;
          productData.priceRange = {
            min: formData.priceRangeMin ? parseFloat(formData.priceRangeMin) : 0,
            max: formData.priceRangeMax ? parseFloat(formData.priceRangeMax) : 0
          };
        }
      } else {
        // If has variants, clear base price
        productData.price = null;
        productData.priceRange = null;
      }

      // Remove undefined values before saving to Firestore
      const cleanedData = removeUndefinedValues(productData);
      await updateDoc(productRef, cleanedData);
      
      toast.success('Product updated successfully');
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    }
  };

  const handleDeleteProduct = async (productId: string, images: string[]) => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete images from storage
      for (const imageUrl of images) {
        try {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      }

      // Delete product document
      const productRef = doc(db, 'published_products', productId);
      await deleteDoc(productRef);
      
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleTogglePublish = async (product: Product) => {
    try {
      const productRef = doc(db, 'published_products', product.id);
      await updateDoc(productRef, {
        isPublished: !product.isPublished,
        updatedAt: new Date()
      });
      
      toast.success(`Product ${!product.isPublished ? 'published' : 'unpublished'}`);
      fetchProducts();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast.error('Failed to update product');
    }
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      categoryId: product.categoryId,
      brand: product.brand,
      price: product.priceType === 'fixed' ? (product.price?.toString() || '') : '',
      priceType: product.priceType || 'fixed',
      priceRangeMin: product.priceRange?.min?.toString() || '',
      priceRangeMax: product.priceRange?.max?.toString() || '',
      baseStock: product.stockType === 'order-basis' ? '0' : product.baseStock,
      stockType: product.stockType || 'in-stock',
      hasVariants: product.hasVariants || false,
      variants: product.variants || [],
      images: product.images,
      specifications: product.specifications,
      isPublished: product.isPublished
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      categoryId: '',
      brand: '',
      price: '',
      priceType: 'fixed',
      priceRangeMin: '',
      priceRangeMax: '',
      baseStock: '0',
      stockType: 'in-stock',
      hasVariants: false,
      variants: [] as ProductVariant[],
      images: [],
      specifications: {},
      isPublished: true
    });
    setSpecKey('');
    setSpecValue('');
  };

  const closeModals = () => {
    setShowAddModal(false);
    setEditingProduct(null);
    resetForm();
  };

  // Variant Management Functions
  const handleAddVariant = () => {
    if (!variantForm.name.trim()) {
      toast.error('Variant name is required');
      return;
    }

    const newVariant: ProductVariant = {
      id: Date.now().toString(),
      name: variantForm.name,
      ...(variantForm.sku && { sku: variantForm.sku }),
      priceType: variantForm.priceType,
      baseStock: variantForm.stockType === 'order-basis' ? 'Order Basis' : variantForm.baseStock,
      stockType: variantForm.stockType,
      ...(variantForm.image && { image: variantForm.image })
    };

    // Add price or price range based on type
    if (variantForm.priceType === 'fixed') {
      if (variantForm.price) {
        newVariant.price = parseFloat(variantForm.price);
      }
    } else {
      newVariant.priceRange = {
        min: variantForm.priceRangeMin ? parseFloat(variantForm.priceRangeMin) : 0,
        max: variantForm.priceRangeMax ? parseFloat(variantForm.priceRangeMax) : 0
      };
    }

    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));

    toast.success('Variant added');
    closeVariantModal();
  };

  const handleUpdateVariant = () => {
    if (!variantForm.name.trim() || !editingVariant) {
      toast.error('Variant name is required');
      return;
    }

    const updatedVariant: ProductVariant = {
      id: editingVariant.id,
      name: variantForm.name,
      ...(variantForm.sku && { sku: variantForm.sku }),
      priceType: variantForm.priceType,
      baseStock: variantForm.stockType === 'order-basis' ? 'Order Basis' : variantForm.baseStock,
      stockType: variantForm.stockType,
      ...(variantForm.image && { image: variantForm.image })
    };

    // Add price or price range based on type
    if (variantForm.priceType === 'fixed') {
      if (variantForm.price) {
        updatedVariant.price = parseFloat(variantForm.price);
      }
    } else {
      updatedVariant.priceRange = {
        min: variantForm.priceRangeMin ? parseFloat(variantForm.priceRangeMin) : 0,
        max: variantForm.priceRangeMax ? parseFloat(variantForm.priceRangeMax) : 0
      };
    }

    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v => v.id === editingVariant.id ? updatedVariant : v)
    }));

    toast.success('Variant updated');
    closeVariantModal();
  };

  const handleDeleteVariant = (variantId: string) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter(v => v.id !== variantId)
    }));
    toast.success('Variant deleted');
  };

  const openVariantModal = (variant?: ProductVariant) => {
    if (variant) {
      setEditingVariant(variant);
      setVariantForm({
        name: variant.name,
        sku: variant.sku || '',
        price: variant.price?.toString() || '',
        priceType: variant.priceType || 'fixed',
        priceRangeMin: variant.priceRange?.min?.toString() || '',
        priceRangeMax: variant.priceRange?.max?.toString() || '',
        baseStock: variant.stockType === 'order-basis' ? '0' : variant.baseStock,
        stockType: variant.stockType || 'in-stock',
        image: variant.image || ''
      });
    } else {
      resetVariantForm();
    }
    setShowVariantModal(true);
  };

  const closeVariantModal = () => {
    setShowVariantModal(false);
    setEditingVariant(null);
    resetVariantForm();
  };

  const resetVariantForm = () => {
    setVariantForm({
      name: '',
      sku: '',
      price: '',
      priceType: 'fixed',
      priceRangeMin: '',
      priceRangeMax: '',
      baseStock: '0',
      stockType: 'in-stock',
      image: ''
    });
  };

  // Filter products based on search
  const filteredProducts = products.filter(product => {
    const searchLower = searchQuery.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.description.toLowerCase().includes(searchLower) ||
      product.brand.toLowerCase().includes(searchLower) ||
      product.categoryName?.toLowerCase().includes(searchLower)
    );
  });

  const displayedProducts = filteredProducts.slice(0, itemsToShow);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Products</h2>
          <p className="text-gray-600 mt-1">
            {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors shadow-lg"
        >
          <IoAdd size={24} />
          Add Product
        </button>
      </div>

      {/* Warning if no categories */}
      {categories.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-yellow-800">
            ⚠️ No categories found. Please create categories first in the "Categories" tab.
          </p>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <IoSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search products by name, description, brand, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Products Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading products...</p>
        </div>
      ) : displayedProducts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-600 text-lg">
            {searchQuery ? 'No products match your search.' : 'No products yet. Click "Add Product" to get started.'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Image</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Brand</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {displayedProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        {product.images.length > 0 ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                            <IoImage className="text-gray-400" size={24} />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500 line-clamp-1">{product.description}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{product.categoryName}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{product.brand}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {product.priceType === 'range' && product.priceRange
                          ? `₱${product.priceRange.min.toFixed(2)} - ₱${product.priceRange.max.toFixed(2)}`
                          : product.price
                          ? `₱${product.price.toFixed(2)}`
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">
                          {product.stockType === 'order-basis' ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              Order Basis
                            </span>
                          ) : (
                            <span>{product.baseStock}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleTogglePublish(product)}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            product.isPublished
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {product.isPublished ? 'Published' : 'Draft'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(product)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit product"
                          >
                            <IoCreate size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product.id, product.images)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete product"
                          >
                            <IoTrash size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Load More Button */}
          {filteredProducts.length > itemsToShow && (
            <div className="text-center">
              <button
                onClick={() => setItemsToShow(prev => prev + 50)}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                Load More ({filteredProducts.length - itemsToShow} remaining)
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Product Modal */}
      {(showAddModal || editingProduct) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <h3 className="text-2xl font-bold text-gray-800">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h3>
              <button
                onClick={closeModals}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <IoClose size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter product name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a category</option>
                    {categories.filter(c => c.isActive).map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brand <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter brand name"
                  />
                </div>

                {/* Stock Type Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stock Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.stockType}
                    onChange={(e) => setFormData({ ...formData, stockType: e.target.value as 'in-stock' | 'order-basis' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="in-stock">In Stock</option>
                    <option value="order-basis">Order Basis</option>
                  </select>
                </div>

                {/* Base Stock - Only show if In Stock */}
                {formData.stockType === 'in-stock' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base Stock
                      {formData.hasVariants && (
                        <span className="ml-2 text-xs text-gray-500">(Calculated from variants)</span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={formData.baseStock}
                      onChange={(e) => setFormData({ ...formData, baseStock: e.target.value })}
                      disabled={formData.hasVariants}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formData.hasVariants ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                      }`}
                      placeholder="0"
                    />
                  </div>
                )}

                {/* Price Type Checkbox */}
                <div className="md:col-span-2">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="priceTypeRange"
                      checked={formData.priceType === 'range'}
                      onChange={(e) => setFormData({ ...formData, priceType: e.target.checked ? 'range' : 'fixed' })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="priceTypeRange" className="text-sm font-medium text-gray-700">
                      Use Price Range instead of Fixed Price
                    </label>
                  </div>

                  {/* Fixed Price */}
                  {formData.priceType === 'fixed' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price (₱)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  ) : (
                    /* Price Range */
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Min Price (₱)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.priceRangeMin}
                          onChange={(e) => setFormData({ ...formData, priceRangeMin: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Max Price (₱)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.priceRangeMax}
                          onChange={(e) => setFormData({ ...formData, priceRangeMax: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.isPublished ? 'published' : 'draft'}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.value === 'published' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter product description"
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Images
                </label>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 cursor-pointer transition-colors">
                      <IoImage size={20} />
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                    </label>
                    {uploadingImage && (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    )}
                  </div>

                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image}
                            alt={`Product ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <IoClose size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Specifications */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specifications
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Key (e.g., Weight)"
                      value={specKey}
                      onChange={(e) => setSpecKey(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Value (e.g., 500g)"
                      value={specValue}
                      onChange={(e) => setSpecValue(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleAddSpecification}
                      className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                    >
                      <IoAdd size={20} />
                    </button>
                  </div>

                  {Object.keys(formData.specifications).length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      {Object.entries(formData.specifications).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center bg-white px-4 py-2 rounded-lg">
                          <span className="text-sm">
                            <span className="font-medium text-gray-700">{key}:</span>{' '}
                            <span className="text-gray-600">{value}</span>
                          </span>
                          <button
                            onClick={() => handleRemoveSpecification(key)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <IoTrash size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Variants Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Variants
                    </label>
                    <p className="text-xs text-gray-500">
                      Add variants for products with different configurations (e.g., RAM sizes, frequencies)
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="hasVariants"
                      checked={formData.hasVariants}
                      onChange={(e) => setFormData({ ...formData, hasVariants: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="hasVariants" className="text-sm font-medium text-gray-700">
                      Enable Variants
                    </label>
                  </div>
                </div>

                {formData.hasVariants && (
                  <div className="space-y-3">
                    <button
                      onClick={() => openVariantModal()}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      <IoAdd size={20} />
                      Add Variant
                    </button>

                    {formData.variants.length > 0 && (
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                        {formData.variants.map((variant) => (
                          <div key={variant.id} className="flex justify-between items-center bg-white px-4 py-3 rounded-lg">
                            <div className="flex items-center gap-3 flex-1">
                              {/* Variant Image Thumbnail */}
                              {variant.image ? (
                                <img
                                  src={variant.image}
                                  alt={variant.name}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                  <IoImage className="text-gray-400" size={24} />
                                </div>
                              )}
                              
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{variant.name}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {variant.sku && <span className="mr-3">SKU: {variant.sku}</span>}
                                  {variant.priceType === 'range' && variant.priceRange
                                    ? <span className="mr-3">₱{variant.priceRange.min.toFixed(2)} - ₱{variant.priceRange.max.toFixed(2)}</span>
                                    : variant.price && <span className="mr-3">₱{variant.price.toFixed(2)}</span>
                                  }
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    variant.stockType === 'order-basis' 
                                      ? 'bg-blue-100 text-blue-800' 
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {variant.stockType === 'order-basis' ? 'Order Basis' : `Stock: ${variant.baseStock}`}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openVariantModal(variant)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit variant"
                              >
                                <IoCreate size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteVariant(variant.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete variant"
                              >
                                <IoTrash size={18} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={editingProduct ? handleUpdateProduct : handleAddProduct}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                >
                  <IoCheckmark size={20} />
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
                <button
                  onClick={closeModals}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  <IoClose size={20} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Crop Modal */}
      {showCropModal && pendingImageFile && (
        <ImageCropModal
          imageFile={pendingImageFile}
          onCropComplete={handleCropComplete}
          onCancel={handleCancelCrop}
        />
      )}

      {/* Variant Modal */}
      {showVariantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100] overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-800">
                {editingVariant ? 'Edit Variant' : 'Add Variant'}
              </h3>
              <button
                onClick={closeVariantModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <IoClose size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Variant Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variant Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={variantForm.name}
                  onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 16GB DDR4 3200MHz"
                />
              </div>

              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SKU (Optional)
                </label>
                <input
                  type="text"
                  value={variantForm.sku}
                  onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., RAM-16GB-3200"
                />
              </div>

              {/* Variant Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variant Image (Optional)
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 cursor-pointer transition-colors">
                      <IoImage size={20} />
                      {uploadingVariantImage ? 'Uploading...' : 'Upload Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleVariantImageUpload}
                        disabled={uploadingVariantImage}
                        className="hidden"
                      />
                    </label>
                    {uploadingVariantImage && (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    )}
                  </div>

                  {variantForm.image && (
                    <div className="relative group inline-block">
                      <img
                        src={variantForm.image}
                        alt="Variant preview"
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={handleRemoveVariantImage}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <IoClose size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stock Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stock Type
                </label>
                <select
                  value={variantForm.stockType}
                  onChange={(e) => setVariantForm({ ...variantForm, stockType: e.target.value as 'in-stock' | 'order-basis' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="in-stock">In Stock</option>
                  <option value="order-basis">Order Basis</option>
                </select>
              </div>

              {/* Base Stock - Only show if In Stock */}
              {variantForm.stockType === 'in-stock' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    value={variantForm.baseStock}
                    onChange={(e) => setVariantForm({ ...variantForm, baseStock: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              )}

              {/* Price Type */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="variantPriceTypeRange"
                    checked={variantForm.priceType === 'range'}
                    onChange={(e) => setVariantForm({ ...variantForm, priceType: e.target.checked ? 'range' : 'fixed' })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="variantPriceTypeRange" className="text-sm font-medium text-gray-700">
                    Use Price Range
                  </label>
                </div>

                {/* Fixed Price or Price Range */}
                {variantForm.priceType === 'fixed' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price (₱)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variantForm.price}
                      onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Min Price (₱)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.priceRangeMin}
                        onChange={(e) => setVariantForm({ ...variantForm, priceRangeMin: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Price (₱)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.priceRangeMax}
                        onChange={(e) => setVariantForm({ ...variantForm, priceRangeMax: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={editingVariant ? handleUpdateVariant : handleAddVariant}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                >
                  <IoCheckmark size={20} />
                  {editingVariant ? 'Update Variant' : 'Add Variant'}
                </button>
                <button
                  onClick={closeVariantModal}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  <IoClose size={20} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Variant Image Crop Modal */}
      {showVariantCropModal && pendingVariantImageFile && (
        <ImageCropModal
          imageFile={pendingVariantImageFile}
          onCropComplete={handleVariantCropComplete}
          onCancel={handleVariantCancelCrop}
        />
      )}
    </div>
  );
};
